import _ from "lodash";
import moment from "moment";
import "moment/locale/pt-br";

// Services
import getApiUrl from "services/getApiUrl";


// Utils
import { showNotification } from "./notification";
import { importApi } from "services/actions/import";
import buildFilterEntryKeepField from "utils/normalize-field-to-request-filter"
import getAllPersistentFilterByReq from "utils/get-field-by-request"
import { normalizeTimeFilterValue } from "utils/use-persistents-filters"

// Types
const FETCH_FORM_DATA_REQUEST = "core/form/FETCH_FORM_DATA_REQUEST";
const FETCH_FORM_DATA_SUCCESS = "core/form/FETCH_FORM_DATA_SUCCESS";
const FETCH_FORM_DATA_FAILURE = "core/form/FETCH_FORM_DATA_FAILURE";
const FETCH_FORM_VALUE_ADD = "core/form/FETCH_FORM_VALUE_ADD";
const FETCH_FORM_VALUE_DELETE_ALL = "core/form/FETCH_FORM_VALUE_DELETE_ALL";
const FETCH_FORM_READONLY_ADD = "core/form/FETCH_FORM_READONLY_ADD";
const FETCH_FORM_READONLY_DELETE = "core/form/FETCH_FORM_READONLY_DELETE";
const FETCH_FORM_READONLY_DELETE_ALL =
  "core/form/FETCH_FORM_READONLY_DELETE_ALL";
const FETCH_LKP_LOADING_START = "core/form/FETCH_LKP_LOADING_START";
const FETCH_LKP_LOADING_STOP = "core/form/FETCH_LKP_LOADING_STOP";

// Reducer
const initialState = {
  loading: true,
  formName: null,
  columns: [],
  records: [],
  fields: [],
  refFields: [],
  refValues: {},
  lkpValues: {},
  lkpLoading: 0,
  queueStatus: {},
  queueErrorsMsg: [],
  total: 0,
  totalInIndex: 0,
  error: "",
};

export default function formDataReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_FORM_DATA_REQUEST:
      if (action.payload?.formName && action.payload.formName !== state.formName) {
        return {
          ...initialState,
          loading: true,
          formName: action.payload.formName,
        };
      }
      return { ...state, loading: true };
    case FETCH_FORM_DATA_SUCCESS:
      // console.log("FETCH_FORM_DATA_SUCCESS", action.payload);
      return {
        ...state,
        loading: false,
        formName: action.payload.formName ?? state.formName,
        columns: action.payload.keys,
        records: action.payload.data,
        fields: action.payload.fields,
        refFields: action.payload.refFields,
        queueStatus: action.payload.queueStatus,
        queueErrorsMsg: action.payload.queueErrorsMsg,
        total: action.payload.total,
        totalInIndex: action.payload.totalInIndex,
        error: "",
      };
    case FETCH_FORM_DATA_FAILURE:
      return {
        ...state,
        loading: false,
        formName: action.payload?.formName ?? state.formName,
        columns: [],
        records: [],
        fields: [],
        refFields: [],
        refValues: {},
        lkpValues: {},
        queueStatus: {},
        queueErrorsMsg: [],
        total: 0,
        totalInIndex: 0,
        error: action.payload?.message ?? action.payload,
      };
    case FETCH_FORM_VALUE_ADD:
      // console.log("FETCH_FORM_VALUE_ADD", action.payload);
      const newRefFieldsLabelsValues = {
        ...state.refValues,
        [action.payload.fieldName]: action.payload.refData,
      };
      return {
        ...state,
        refValues: newRefFieldsLabelsValues,
      };
    case FETCH_FORM_VALUE_DELETE_ALL:
      return {
        ...state,
        refValues: {},
      };
    case FETCH_FORM_READONLY_ADD:
      // console.log("FETCH_FORM_READONLY_ADD", action.payload);
      const currentList = state.lkpValues[action.payload.fieldName] || [];
      const incomingData = action.payload.lkpData;

      // Ensure we don't duplicate by _id
      const exists = currentList.find(item => String(item._id) === String(incomingData._id));

      let newList;
      if (exists) {
        // Replace existing if found (to update value)
        newList = currentList.map(item => String(item._id) === String(incomingData._id) ? incomingData : item);
      } else {
        newList = [...currentList, incomingData];
      }
      //console.log("New List for", action.payload.fieldName, newList);

      const newLkpValues = {
        ...state.lkpValues,
        [action.payload.fieldName]: newList,
      };
      return {
        ...state,
        lkpValues: newLkpValues,
      };
    case FETCH_FORM_READONLY_DELETE:
      // console.log("FETCH_FORM_READONLY_DELETE", action.payload);
      const newLkpRemoved = _.omit(state.lkpValues, action.payload.forRemove);
      return {
        ...state,
        lkpValues: newLkpRemoved,
      };
    case FETCH_FORM_READONLY_DELETE_ALL:
      return {
        ...state,
        lkpValues: {},
      };
    case FETCH_LKP_LOADING_START:
      return {
        ...state,
        lkpLoading: (state.lkpLoading || 0) + 1,
      };
    case FETCH_LKP_LOADING_STOP:
      return {
        ...state,
        lkpLoading: Math.max(0, (state.lkpLoading || 0) - 1),
      };
    default:
      return state;
  }
}

const getFieldsColumns = (columns, formName) => {
  // Se a coluna ou o nome estiver vazio, retorna array vazio
  if (!formName || !columns || columns.length === 0) {
    return [];
  }

  const config = columns
    .filter(c => !c.hideInSearch)
    .map(c => buildFilterEntryKeepField(c.field ?? c.title, c, formName));
  return config;
};

// Actions
export const fetchFormData = (form, page, pageSize, createdAt, queryParams, sorter = null, location = null, navigate = null, formRef = null, isRegistryForm = false) => {
  return async (dispatch) => {
    dispatch({ type: FETCH_FORM_DATA_REQUEST, payload: { formName: form } });
    try {
      const normalizeDateRange = (rawValue) => {
        if (_.isNil(rawValue)) return null;

        const [rawStart, rawEnd] = _.isArray(rawValue)
          ? [rawValue[0], rawValue[1]]
          : [rawValue, rawValue];

        const parseDate = (value) => {
          if (_.isNil(value) || value === "") return null;
          if (moment.isMoment(value)) {
            return value.isValid() ? value.clone() : null;
          }
          if (_.isNumber(value)) {
            const fromNumber = moment(value);
            return fromNumber.isValid() ? fromNumber : null;
          }
          if (_.isString(value)) {
            const strictParsed = moment(
              value,
              [
                "DD/MM/YYYY HH:mm:ss",
                "DD/MM/YYYY HH:mm",
                "DD/MM/YYYY",
                "YYYY-MM-DDTHH:mm:ss[Z]",
                "YYYY-MM-DDTHH:mm:ss.SSS[Z]",
                "YYYY-MM-DD",
              ],
              true
            );
            if (strictParsed.isValid()) return strictParsed;
          }
          if (_.isFunction(value?.toDate)) {
            const fromToDate = moment(value.toDate());
            return fromToDate.isValid() ? fromToDate : null;
          }
          const parsed = moment(value);
          return parsed.isValid() ? parsed : null;
        };

        const start = parseDate(rawStart) || parseDate(rawEnd);
        const end = parseDate(rawEnd) || parseDate(rawStart);

        if (!start || !end) return null;
        return [start.startOf("day"), end.endOf("day")];
      };

      const normalizeCreatedAtDate = (rawValue) => {
        const normalizedRange = normalizeDateRange(rawValue);
        if (!normalizedRange) return null;
        const [startDate] = normalizedRange;
        return startDate.format("DD/MM/YYYY");
      };

      const hasMeaningfulFilterValue = (value) => {
        if (_.isNil(value)) return false;
        if (_.isString(value)) return value.trim() !== "";
        if (_.isArray(value)) return value.some((item) => hasMeaningfulFilterValue(item));
        return true;
      };

      const fields = await getApiUrl.get(
        `/objetos/${form}/campos/todos?withRef=true`
      );
      const { message: fieldsMessage } = fields.data;
      const sortedFields = _.uniqBy(fieldsMessage || [], "field").sort(
        (a, b) => a.index - b.index
      );
      const sortBy = [];
      if (sorter && Object.entries(sorter).length > 0) {
        for (const key in sorter) {
          if (!sorter[key]) continue;
          let index = fieldsMessage.findIndex(item => item.field === key);
          if (index !== -1) {
            sortBy.push({
              field: fieldsMessage[index]['field'],
              sort: sorter[key],
              fieldType: fieldsMessage[index]['fieldtype'],
              returnType: fieldsMessage[index]['returntype']
            });
          } else {
            sortBy.push({
              field: key,
              sort: sorter[key]
            });
          }
        }
      }
      const query = [];
      // Verifica se existe campos
      if (fieldsMessage) {

        // Caso seja a parte de formulários, realiza a requisição
        if (isRegistryForm) {
          // Inclui o filtro por ID do registro (_id), que não é um campo dinâmico do formulário
          const columnsFilter = [
            buildFilterEntryKeepField("_id", {}, form),
            ...getFieldsColumns(fieldsMessage, form),
          ];
          const persistentFilters = getAllPersistentFilterByReq(columnsFilter, location, navigate, formRef);
          queryParams = {
            ...persistentFilters,
            ...queryParams
          };
        }

        let createdAtFilter = createdAt;
        if (_.isNil(createdAtFilter) && !_.isNil(queryParams?.createdAt)) {
          createdAtFilter = queryParams.createdAt;
        }
        if (_.isNil(createdAtFilter) && formRef?.current) {
          let createdAtFromForm = null;
          if (_.isFunction(formRef.current?.getFieldValue)) {
            createdAtFromForm = formRef.current.getFieldValue("createdAt");
          } else if (_.isFunction(formRef.current?.getFieldsValue)) {
            createdAtFromForm = formRef.current.getFieldsValue()?.createdAt;
          }
          if (hasMeaningfulFilterValue(createdAtFromForm)) {
            createdAtFilter = createdAtFromForm;
          }
        }
        createdAtFilter = normalizeCreatedAtDate(createdAtFilter);

        if (!_.isNil(queryParams?.createdAt)) {
          queryParams = _.omit(queryParams, "createdAt");
        }
        queryParams = _.pickBy(queryParams, (value) => hasMeaningfulFilterValue(value));

        // Array com nome de todos os campos
        const fieldNames = sortedFields.map((y) => y.field);
        // Array com todos os campos referenciados e consulta
        const onlyRefFields = _.flatMap(sortedFields, "refFields");
        const onlyLkpFields = _.flatMap(sortedFields, "getValueOf");
        // Verifica se está filtrando a tabela
        if (Object.keys(queryParams).length > 0) {
          sortedFields.map(async (data) => {
            const { field: fieldName, fieldtype: fieldType, returntype = null, father = null } = data;
            const fieldValue = queryParams[fieldName];
            if (hasMeaningfulFilterValue(fieldValue)) {
              let fieldMapping;
              switch (fieldType) {
                case "Date":
                  if (_.isArray(fieldValue)) {
                    const [start, end] = fieldValue;
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          script: {
                            script: {
                              source: `
                  if (!doc.containsKey(params.field) || doc[params.field].empty) return false;
                  String dateString = doc[params.field].value;
                  if (dateString == '') return false;
                  ZonedDateTime fieldDate = ZonedDateTime.parse(dateString);
                  ZonedDateTime startDate = ZonedDateTime.parse(params.startDate);
                  ZonedDateTime endDate = ZonedDateTime.parse(params.endDate);
                  return !fieldDate.isBefore(startDate) && !fieldDate.isAfter(endDate);
                `,
                              params: {
                                field: `${fieldName}.value.keyword`,
                                startDate: moment.utc({ year: moment(Number(start)).year(), month: moment(Number(start)).month(), date: moment(Number(start)).date() }).startOf('day').toISOString(),
                                endDate: moment.utc({ year: moment(Number(end)).year(), month: moment(Number(end)).month(), date: moment(Number(end)).date() }).endOf('day').toISOString()
                              },
                              lang: "painless"
                            }
                          }
                        }
                      }
                    };
                  } else {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: moment(fieldValue, 'DD/MM/YYYY').startOf('day').format('YYYY-MM-DDTHH:mm:ss[Z]') ?? "",
                          },
                        },
                      },
                    }
                  }
                  break;
                case "refLookup":
                  if (fieldValue['fatherFieldType'] && fieldValue['fatherFieldType'] === 'Date' && _.isArray(fieldValue)) {
                    const [start, end] = fieldValue;
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          script: {
                            script: {
                              source: `
                  if (!doc.containsKey(params.field) || doc[params.field].empty) return false;
                  String dateString = doc[params.field].value;
                  if (dateString == '') return false;
                  ZonedDateTime fieldDate = ZonedDateTime.parse(dateString);
                  ZonedDateTime startDate = ZonedDateTime.parse(params.startDate);
                  ZonedDateTime endDate = ZonedDateTime.parse(params.endDate);
                  return !fieldDate.isBefore(startDate) && !fieldDate.isAfter(endDate);
                `,
                              params: {
                                field: `${fieldName}.value.keyword`,
                                startDate: moment.utc(start).startOf('day').toISOString(),
                                endDate: moment.utc(end).startOf('day').toISOString()
                              },
                              lang: "painless"
                            }
                          }
                        }
                      }
                    };
                  } else if (_.isString(fieldValue) || _.isNumber(fieldValue)) {
                    const value = moment(fieldValue, 'DD/MM/YYYY', true).isValid() ? moment(fieldValue, 'DD/MM/YYYY').format('YYYY-MM-DDT00:00:00[Z]') : (_.isString(fieldValue) ? fieldValue.trim() : fieldValue) ?? "";
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: value,
                          },
                        },
                      },
                    }
                  } else if (_.isArray(fieldValue)) {
                    const terms = fieldValue
                      .filter((item) => hasMeaningfulFilterValue(item))
                      .map((item) => _.isString(item) ? item.trim() : item);
                    if (terms.length === 0) return;
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          terms: {
                            [`${fieldName}.value`]: terms,
                          },
                        },
                      },
                    }
                  }

                  break;
                case "Numeric":
                  fieldMapping = {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value`]: fieldValue ?? "",
                        },
                      },
                    },
                  }
                  break
                case 'formula':
                  fieldMapping = (returntype === "Numeric") ? {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value`]: !_.isNil(fieldValue) ? fieldValue : "",
                        },
                      },
                    },
                  } : {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value.keyword`]: fieldValue ?? "",
                        },
                      },
                    },
                  }
                  break
                case "Text":
                  fieldMapping = {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value.keyword`]: _.isString(fieldValue) ? fieldValue.trim() : (fieldValue ?? ""),
                        },
                      },
                    },
                  }
                  break;
                case "Time":
                  // O registro armazena o tempo como "HH:mm"; normaliza o valor
                  // do filtro (moment/timestamp em ms/string) para o mesmo formato
                  fieldMapping = {
                    nested: {
                      path: fieldName,
                      query: {
                        match: {
                          [`${fieldName}.value.keyword`]: normalizeTimeFilterValue(fieldValue) ?? "",
                        },
                      },
                    },
                  }
                  break;
                case "refUniqueSelection":
                case "refMultipleSelection":
                  if (_.isArray(fieldValue)) {
                    const refTerms = fieldValue
                      .filter((item) => hasMeaningfulFilterValue(item))
                      .map((item) => {
                        if (moment(item, 'DD/MM/YYYY', true).isValid()) return moment(item, 'DD/MM/YYYY').format('YYYY-MM-DDT00:00:00[Z]');
                        return _.isString(item) ? item.trim() : item;
                      });
                    if (refTerms.length > 0) {
                      fieldMapping = {
                        nested: {
                          path: fieldName,
                          query: {
                            terms: {
                              [`${fieldName}.value.keyword`]: refTerms,
                            },
                          },
                        },
                      };
                    }
                  } else {
                    const value = moment(fieldValue, 'DD/MM/YYYY', true).isValid() ? moment(fieldValue, 'DD/MM/YYYY').format('YYYY-MM-DDT00:00:00[Z]') : (_.isString(fieldValue) ? fieldValue.trim() : (fieldValue ?? ""));
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          match: {
                            [`${fieldName}.value.keyword`]: value,
                          },
                        },
                      },
                    };
                  }
                  break;
                case "uniqueSelection":
                case "multipleSelection":
                  let terms = (_.isArray(fieldValue) ? fieldValue : [fieldValue])
                    .filter((item) => hasMeaningfulFilterValue(item))
                    .map((item) => _.isString(item) ? item.trim() : item);
                  if (terms.length > 0) {
                    fieldMapping = {
                      nested: {
                        path: fieldName,
                        query: {
                          terms: {
                            [`${fieldName}.value.keyword`]: terms,
                          },
                        },
                      },
                    };
                  } else {
                    return;
                  }

                  break;
                default:
                  fieldMapping = {};
              }
              query.push(fieldMapping);
            }
          });
          const recordIdFilter = _.trim(queryParams._id || queryParams.id || "");
          if (recordIdFilter) {
            query.push({
              term: { _id: recordIdFilter }
            })
          }
        }

        const refFields = await getApiUrl.post(
          `/objetos/campos/pesquisar/id`,
          {
            fields: _.compact(_.concat(onlyRefFields, onlyLkpFields)),
          }
        );

        const records = await getApiUrl.post(
          `/objetos/formularios/dados/paginado?index=${form}&page=${page}&pageSize=${pageSize}&isFilter=${Object.keys(queryParams).length > 0 || query.length > 0 || createdAtFilter ? true : false
          }`,
          {
            query,
            createdAt: createdAtFilter,
            showOnly: [...fieldNames, "createdAt", "updatedAt"],
            sorter: sortBy
          }
        );
        if (records && records.data.success) {
          const {
            message: recordsMessage,
            total: totalMessage,
            totalInIndex: totalInIndexMessage,
          } = records.data;
          // Obtém as chaves de cada campo
          const allKeys = _.map(sortedFields, "field");
          dispatch({
            type: FETCH_FORM_DATA_SUCCESS,
            payload: {
              formName: form,
              keys: allKeys,
              data: recordsMessage,
              fields: sortedFields,
              refFields: refFields?.data.message || [],
              total: totalMessage || 0,
              totalInIndex: totalInIndexMessage || 0,
            },
          });
        } else {
          dispatch({
            type: FETCH_FORM_DATA_FAILURE,
            payload: {
              formName: form,
              message:
                "Nada encontrado ao tentar obter o histórico do formulário, atualize a página e tente novamente!",
            },
          });
        }
      } else {
        dispatch({
          type: FETCH_FORM_DATA_FAILURE,
          payload: {
            formName: form,
            message:
              "Não encontramos nenhum campo no formulário, atualize a página e tente novamente!",
          },
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_FORM_DATA_FAILURE,
        payload: { formName: form, message: error.message },
      });
    }
  };
};

export const fetchImportFormData = (form, queueId) => {
  return async (dispatch) => {
    dispatch({ type: FETCH_FORM_DATA_REQUEST, payload: { formName: form } });
    try {
      const fields = await getApiUrl.get(
        `/objetos/${form}/campos/todos?withRef=true`
      );
      const { message: fieldsMessage } = fields.data;
      const sortedFields = _.uniqBy(fieldsMessage || [], "field").sort(
        (a, b) => a.index - b.index
      );
      // Verifica se existe campos
      if (fieldsMessage) {
        // Array com todos os campos referenciados e consulta
        const onlyRefFields = _.flatMap(sortedFields, "refFields");
        const onlyLkpFields = _.flatMap(sortedFields, "getValueOf");
        const refFields = await getApiUrl.post(`/objetos/campos/pesquisar/id`,
          {
            fields: _.compact(_.concat(onlyRefFields, onlyLkpFields)),
          }
        );
        // Array com todos os registros da importação
        const importRecords = await importApi.filesHistoryById(queueId);
        // Array com todos os erros encontrados durante a importação
        const importRecordsErrors = await importApi.filesHistoryErrosMsgByForm(form, queueId);
        if (
          importRecords &&
          importRecordsErrors &&
          importRecords.success
        ) {
          const { message: recordsMessage } = importRecords;
          const { message: recordsErrorsMessage } = importRecordsErrors;
          // Separa os registros já reenviados
          const updatedRecords =
            recordsMessage[0].errorDocuments.filter(
              (errorDoc) => !recordsMessage[0].clearDocs.includes(errorDoc._id)
            ) || [];
          // Obtém as chaves de cada campo
          const allKeys = _.map(sortedFields, "field");
          // Mantém em cache os detalhes da importação
          dispatch({
            type: FETCH_FORM_DATA_SUCCESS,
            payload: {
              formName: form,
              keys: allKeys,
              data: updatedRecords || [],
              fields: sortedFields,
              refFields: refFields?.data.message || [],
              // errorDocuments já está em `data`; guardar outra cópia no
              // queueStatus dobra a memória em importações grandes.
              queueStatus: _.omit(recordsMessage[0] || {}, ["errorDocuments"]),
              queueErrorsMsg: recordsErrorsMessage || [],
              total: _.size(updatedRecords),
            },
          });
        } else {
          dispatch({
            type: FETCH_FORM_DATA_FAILURE,
            payload: {
              formName: form,
              message:
                "Nada encontrado ao tentar obter o histórico da importação, atualize a página e tente novamente!",
            },
          });
        }
      } else {
        dispatch({
          type: FETCH_FORM_DATA_FAILURE,
          payload: {
            formName: form,
            message:
              "Não encontramos nenhum campo no formulário, atualize a página e tente novamente!",
          },
        });
      }
    } catch (error) {
      dispatch({
        type: FETCH_FORM_DATA_FAILURE,
        payload: { formName: form, message: error.message },
      });
    }
  };
};

export const addFormData = (fieldName, fieldValue) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: FETCH_FORM_READONLY_ADD,
        payload: {
          fieldName: fieldName,
          lkpData: fieldValue,
        },
      });
    } catch (error) {
      dispatch(
        showNotification(
          `Algo deu errado ao tentar anexar os resultados do campo ${fieldName} ao formulário, tente novamente!`,
          "error"
        )
      );
    }
  };
};

export const deleteFormData = (fields) => {
  return async (dispatch) => {
    dispatch({
      type: FETCH_FORM_READONLY_DELETE,
      payload: {
        forRemove: fields,
      },
    });
  };
};

export const deleteAllFormData = () => {
  return async (dispatch) => {
    dispatch({
      type: FETCH_FORM_READONLY_DELETE_ALL,
    });
  };
};

// Sinaliza o início do carregamento dos campos de retorno (lookup)
export const startLkpLoading = () => ({
  type: FETCH_LKP_LOADING_START,
});

// Sinaliza o fim do carregamento dos campos de retorno (lookup)
export const stopLkpLoading = () => ({
  type: FETCH_LKP_LOADING_STOP,
});

export const addRefFieldsFormData = (fieldName, fieldValue) => {
  return async (dispatch) => {
    try {
      dispatch({
        type: FETCH_FORM_VALUE_ADD,
        payload: {
          fieldName: fieldName,
          refData: fieldValue,
        },
      });
    } catch (error) {
      dispatch(
        showNotification(
          `Algo deu errado ao tentar anexar os resultados do campo ${fieldName} ao formulário, tente novamente!`,
          "error"
        )
      );
    }
  };
};

export const deleteAllRefFieldsFormData = () => {
  return async (dispatch) => {
    dispatch({
      type: FETCH_FORM_VALUE_DELETE_ALL,
    });
  };
};

export const validateFormula = async (value, formName) => {
  return await getApiUrl
    .post(`/objetos/formularios/campos/formula/validar`, {
      formula: value,
      formName
    })
    .then((response) => {
      return response.data;
    })
    .catch((err) => {
      return err.response.data;
    });
}
